/**
 * Mapa de erros de regras de negócio da API.
 *
 * Cada entrada define:
 *   keywords          → string[] — palavras-chave (match case-insensitive, qualquer uma basta)
 *   titleKey          → string — chave i18n para o título do modal
 *   messageKey        → string — chave i18n para a mensagem (ignorado se translateMessage: false)
 *   translateMessage  → boolean — true: usa messageKey com i18n; false: usa a mensagem bruta da API
 *   primaryButton     → { labelKey, action } — "close" fecha o modal; qualquer fn é chamada diretamente
 *   secondaryButton?  → { labelKey, action } — opcional
 *
 * Adicionar novos erros aqui. A primeira regra que der match vence.
 */
const API_ERROR_MESSAGES = [
  // booking_validate_limit — limite de agendamentos em aberto
  {
    keywords: [
      "Você atingiu o número máximo de agendamentos em aberto permitido",
      "You have reached the maximum number of open reservations allowed",
      "Has alcanzado el número máximo de reservas abiertas permitidas",
    ],
    titleKey: "openReservationLimitTitle",
    translateMessage: false,
    primaryButton: { labelKey: "understood", action: "close" },
  },
  // booking_validate_limit_by_plan — limite do plano (requer upgrade)
  {
    id: "clientBlocked",
    keywords: [
      "Você não consegue realizar o agendamento com este estabelecimento",
      "You cannot make an appointment with this establishment",
      "No puedes realizar la cita con este establecimiento",
    ],
    titleKey: "blockedAppointmentTitle",
    messageKey: "blockedAppointmentMessage",
    suppressSecondaryButton: true,
    primaryButton: { labelKey: "understood", action: "close" },
  },
  {
    id: "appointmentLimitPlan",
    keywords: [
      "Você atingiu o número máximo de agendamentos permitido no seu plano. Por favor faça o upgrade",
      "You have reached the maximum number of appointments allowed on your plan. Please upgrade",
      "Has alcanzado el número máximo de citas permitidas en tu plan. Por favor, actualiza tu plan.",
    ],
    titleKey: "appointmentLimitTitle",
    messageKey: "appointmentLimitMessage",
    primaryButton: { labelKey: "understood", action: "close" },
    secondaryButton: { labelKey: "notifyOnWhatsApp" },
  },
  // booking_professional_unavailable_reservation — profissional com agendamento online desativado
  // (PRIT-4127). Exibe o mesmo modal do limite do plano.
  {
    id: "professionalUnavailableReservation",
    keywords: [
      "Profissional não tem disponibilidade",
      "Professional has no availability",
      "El profesional no tiene disponibilidad",
    ],
    titleKey: "appointmentLimitTitle",
    messageKey: "appointmentLimitMessage",
    primaryButton: { labelKey: "understood", action: "close" },
    secondaryButton: { labelKey: "notifyOnWhatsApp" },
  },
];

/** Erros que exibem o modal de limite de agendamento (com botão de WhatsApp). */
export const APPOINTMENT_LIMIT_MODAL_IDS = ["appointmentLimitPlan", "professionalUnavailableReservation"];

export const extractApiMessage = (error) =>
  error?.response?.data?.message || error?.message || "";

/**
 * Retorna true se o erro corresponde ao limite de agendamentos do plano (upgrade necessário).
 */
export function isAppointmentLimitPlanError(error) {
  return getApiErrorConfig(error)?.id === "appointmentLimitPlan";
}

/**
 * Retorna true se o erro corresponde à regra de antecedência mínima do estabelecimento.
 * Neste caso o caller deve acionar o fluxo de slots alternativos (não um modal genérico).
 *
 * NOTA: a API retorna esta mensagem hardcoded apenas em PT (booking_transaction_service.rb),
 * sem variantes EN/ES nas locale files — o match em PT cobre todos os idiomas.
 */
export function isAdvanceNoticeError(error) {
  const message = extractApiMessage(error);
  if (!message || typeof message !== "string") return false;
  const lower = message.toLowerCase();
  return [
    "O horário inicial da reserva é maior que o horário de antecedência criado pelo estabelecimento",
  ].some((kw) => lower.includes(kw.toLowerCase()));
}

/**
 * Retorna a configuração do modal para o erro recebido,
 * ou null se nenhuma regra corresponder (usar fallback genérico).
 */
export function getApiErrorConfig(error) {
  const message = extractApiMessage(error);
  if (!message || typeof message !== "string") return null;

  const lower = message.toLowerCase();
  return (
    API_ERROR_MESSAGES.find((entry) =>
      entry.keywords.some((kw) => lower.includes(kw.toLowerCase()))
    ) ?? null
  );
}
